import React, { useState } from 'react';
import { apiService } from '../../services/api';
import { useMutation } from '../../hooks/useApi';
import logger from '../../utils/logger';
import { Eye, FileDown, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

function RaportShitje() {
  const { roli } = useAuth();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [shitjet, setShitjet] = useState({});
  const [error, setError] = useState('');
  const [faqjaAktuale, setFaqjaAktuale] = useState({});
  const perFaqe = 10;

  const { mutate: fetchRaport, loading: loadingRaport } = useMutation(
    async ({ from, to }) => {
      return await apiService.get('/raport-shitje', { params: { from, to } });
    },
    {
      onSuccess: (data) => {
        const faqet = {};
        for (const key in data) {
          faqet[key] = 1;
        }
        setFaqjaAktuale(faqet);
        setShitjet(data);
        setError('');
      },
      onError: () => {
        setError('Nuk mund të merret raporti i shitjeve.');
      },
      showNotifications: false
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    fetchRaport({ from, to });
  };

  const handleExportPdf = async () => {
    if (!from || !to) {
      setError('Ju lutem zgjidhni datat për PDF.');
      return;
    }
    try {
      const response = await apiService.get('/raport-shitje/pdf', {
        params: { from, to },
        responseType: 'blob'
      });
      const file = new Blob([response], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    } catch (err) {
      logger.error('PDF export error:', err);
      setError('Dështoi shkarkimi i PDF-it.');
    }
  };

  const renderTabela = (data, lokacioni) => {
    const totali = data.reduce((sum, s) => sum + (Number(s.totali) || 0), 0);
    const faqja = faqjaAktuale[lokacioni] || 1;
    const offset = (faqja - 1) * perFaqe;
    const faqetTotale = Math.ceil(data.length / perFaqe);
    const faqjaData = data.slice(offset, offset + perFaqe);

    return (
      <div key={lokacioni} className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Lokacioni: {lokacioni}</h3>

        <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
          <span>Faqe {faqja} nga {faqetTotale}</span>
          <div className="flex gap-1">
            <button onClick={() => setFaqjaAktuale(prev => ({ ...prev, [lokacioni]: 1 }))} disabled={faqja === 1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsLeft size={16} /></button>
            <button onClick={() => setFaqjaAktuale(prev => ({ ...prev, [lokacioni]: faqja - 1 }))} disabled={faqja === 1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft size={16} /></button>
            <button onClick={() => setFaqjaAktuale(prev => ({ ...prev, [lokacioni]: faqja + 1 }))} disabled={faqja === faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight size={16} /></button>
            <button onClick={() => setFaqjaAktuale(prev => ({ ...prev, [lokacioni]: faqetTotale }))} disabled={faqja === faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsRight size={16} /></button>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <div className="card-premium overflow-hidden p-0">
            <table className="table-premium">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Produkti</th>
                  <th>Madhësia</th>
                  <th>Ngjyra</th>
                  <th>Shitësi</th>
                  <th className="text-right">Sasia</th>
                  <th className="text-right">Çmimi</th>
                  <th className="text-right">Totali</th>
                </tr>
              </thead>
              <tbody>
                {faqjaData.map((s, i) => (
                  <tr key={i}>
                    <td>{s.data}</td>
                    <td>{s.produkti}</td>
                    <td>{s.madhesia}</td>
                    <td>{s.ngjyrat}</td>
                    <td>{s.shitesi}</td>
                    <td className="text-right">{s.sasia}</td>
                    <td className="text-right">{Number(s.cmimi || 0).toFixed(2)} Den</td>
                    <td className="text-right font-semibold">{Number(s.totali || 0).toFixed(2)} Den</td>
                  </tr>
                ))}
                <tr className="bg-gray-100 dark:bg-dark-700 font-semibold">
                  <td colSpan="7" className="text-right">Totali:</td>
                  <td className="text-right text-green-700 dark:text-green-400">{totali.toFixed(2)} Den</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-4">
          {faqjaData.map((s, i) => (
            <div key={i} className="card-premium p-4 space-y-1">
              <div><strong className="text-gray-700 dark:text-gray-300">Data:</strong> {s.data}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Produkti:</strong> {s.produkti}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Madhësia:</strong> {s.madhesia}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Ngjyra:</strong> {s.ngjyrat}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Shitësi:</strong> {s.shitesi}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Sasia:</strong> {s.sasia}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Çmimi:</strong> {Number(s.cmimi || 0).toFixed(2)} Den</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Totali:</strong> <span className="font-semibold">{Number(s.totali || 0).toFixed(2)} Den</span></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">📈 Raporti i Shitjeve</h2>

      <form onSubmit={handleSubmit} className="card-premium p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="form-label">Nga data</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input-premium" required />
        </div>
        <div>
          <label className="form-label">Deri më</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input-premium" required />
        </div>
        <button type="submit" className="btn-premium mt-auto flex items-center justify-center gap-2">
          <Eye size={18} /> Shfaq
        </button>
      </form>

      {error && <p className="p-3 rounded-lg bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300 font-medium">{error}</p>}

      {Object.keys(shitjet).length > 0 && (
        <div className="space-y-10">
          <button onClick={handleExportPdf} className="btn-premium flex items-center gap-2">
            <FileDown size={18} /> Shkarko PDF
          </button>

          {roli === 'admin'
            ? Object.entries(shitjet).map(([lok, data]) => renderTabela(data, lok))
            : (() => {
                const emrat = Object.keys(shitjet);
                const lok = emrat.length > 0 ? emrat[0] : 'Lokacioni Im';
                const data = shitjet[lok] || [];
                return renderTabela(data, lok);
              })()}
        </div>
      )}
    </div>
  );
}

export default RaportShitje;
